// One row in the alerts list. Presentational only — children (toggle,
// threshold edit, test-fire) are slotted by the calling widget so the
// entity layer does not depend on features (FSD discipline).

import type { ReactNode } from "react";

import type { AlertRuleResponse } from "@community-analytics/shared";

import { cn } from "@shared/lib";

import {
  ALERT_TYPE_DESCRIPTIONS,
  ALERT_TYPE_LABELS,
  formatThresholdValue,
} from "../lib/copy";

export interface AlertRuleRowProps {
  rule: AlertRuleResponse;
  /** Slotted enabled-toggle (features/toggle-alert-rule). */
  toggleSlot?: ReactNode;
  /** Slotted threshold-edit input (features/edit-alert-threshold). */
  thresholdSlot?: ReactNode;
  /** Slotted test-fire button + result (features/test-fire-alert). */
  testFireSlot?: ReactNode;
  /** Slotted destructive delete control. */
  deleteSlot?: ReactNode;
  className?: string;
}

export function AlertRuleRow({
  rule,
  toggleSlot,
  thresholdSlot,
  testFireSlot,
  deleteSlot,
  className,
}: AlertRuleRowProps) {
  const dimmed = !rule.enabled;
  return (
    <li
      data-alert-rule-id={rule.id}
      data-alert-rule-type={rule.type}
      className={cn(
        "rounded-lg border bg-background p-4",
        dimmed && "opacity-70",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-medium">
            {ALERT_TYPE_LABELS[rule.type]}
          </span>
          <span className="mt-0.5 text-xs text-muted-foreground">
            {ALERT_TYPE_DESCRIPTIONS[rule.type]}
          </span>
        </div>
        {toggleSlot ? <div className="flex-none">{toggleSlot}</div> : null}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Threshold
        </span>
        {thresholdSlot ? (
          <div className="flex-1">{thresholdSlot}</div>
        ) : (
          <span className="text-sm tabular-nums">
            {formatThresholdValue(rule.type, rule.threshold)}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {testFireSlot}
        {deleteSlot}
      </div>
    </li>
  );
}
